import {
  Component,
  computed,
  HostBinding,
  inject,
  input,
  PLATFORM_ID,
  signal,
} from '@angular/core';

import { MediaComponent } from '../media/media.component';
import { BOProduct, MediaContainer } from '@boels/models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  AddToCartButtonClass,
  AddToCartButtonComponent,
} from '../add-to-cart-button/add-to-cart-button.component';
import { ProductEcoComponent } from '../product-eco/product-eco.component';
import { ProductPriceComponent } from '../product-price/product-price.component';
import { CartFacade, CmsRouterLinkDirective } from '@boels/shared-state';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { asyncScheduler, observeOn, switchMap } from 'rxjs';
import {
  ProductIdentifierComponent,
  ProductInCartLabelComponent,
} from '@boels/elements';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { findNestedObjRecursive } from '@boels/helpers';
import { isPlatformBrowser } from '@angular/common';
import { BoelsSkeletonComponent } from '../skeleton/boels-skeleton.component';

@Component({
  imports: [
    MediaComponent,
    FontAwesomeModule,
    AddToCartButtonComponent,
    ProductEcoComponent,
    ProductPriceComponent,
    CmsRouterLinkDirective,
    ProductInCartLabelComponent,
    ProductIdentifierComponent,
    BoelsSkeletonComponent,
  ],
  selector: 'boels-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
})
export class ProductCardComponent {
  /** Injected service */
  #cartFacade = inject(CartFacade);
  #platformId = inject(PLATFORM_ID);

  $product = input.required<BOProduct>();
  $openInNewTab = input<boolean>(false);
  $orderEntry = toSignal(
    toObservable(this.$product).pipe(
      switchMap((product) =>
        this.#cartFacade
          .getProductOrderEntry(product)
          .pipe(observeOn(asyncScheduler)),
      ),
    ),
  );
  $image = computed(() => this.#setImage(this.$product()));
  $ecoLabel = computed(() => this.#setEcoLabel(this.$product()));
  $isPlatformBrowser = signal(isPlatformBrowser(this.#platformId));

  addToCartBtnClass = AddToCartButtonClass;
  faArrowRight = faArrowRight;
  @HostBinding('class') get isAlreadyInCart() {
    const alreadyInCart = this.$orderEntry() ? 'already-in-cart' : '';
    const hasMultipleVariants = this.$product().variantOptions
      ? 'has-multiple-variants'
      : '';
    return `${alreadyInCart} ${hasMultipleVariants}`;
  }

  #setImage(product: BOProduct | undefined): MediaContainer | undefined {
    if (!product) return undefined;
    const image = product.variantOptions
      ? product.baseProductImages
      : product.images;
    return image
      ? image.find(
          (image: { format: string }) => image && image.format === 'product',
        )
      : undefined;
  }

  #setEcoLabel(
    product: BOProduct | undefined,
  ): { hasLabel: boolean; value: string } | undefined {
    if (!product) return undefined;
    const eco = findNestedObjRecursive(
      this.$product()?.classifications,
      'code',
      'Symbol_16',
    );
    return eco ? { hasLabel: true, value: eco['value'] } : undefined;
  }
}
